import { useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import useEvent from 'react-use-event-hook'
import { useQueryClient } from '@tanstack/react-query'

import { CloudArrowUpIcon } from '@gto/icons-react/outline/cloud-arrow-up'
import { Button } from '@gto/ui/button'
import { Typography } from '@gto/ui/typography'

import type { AffiliateAssetSchema } from '@/api/gen'
import {
  getListAssetsQueryKey,
  useDeleteAsset,
  useListAssets,
  useRenameAsset,
  useUploadAsset,
} from '@/api/gen'
import { AppDialog } from '@/components/app-dialog'
import { AssetCard } from '@/components/asset-card'
import { AssetGrid } from '@/components/asset-grid'
import { AssetGridSkeleton } from '@/components/asset-grid-skeleton'
import { AssetPreviewDialog } from '@/components/asset-preview-dialog'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { EmptyState } from '@/components/empty-state'
import { SelectField, TextField } from '@/components/form'
import { InlineQueryError } from '@/components/inline-query-error'
import { MutationError } from '@/components/mutation-error'
import { Page } from '@/components/page'
import { TableFilterBar } from '@/components/table-filter-bar'
import { useMutationErrorHandler } from '@/hooks/use-mutation-error-handler'
import { useReportQueryError } from '@/hooks/use-report-query-error'

const CATEGORY_OPTION_KEYS = [
  { value: 'ALL', labelId: 'admin.assets.category.all' },
  { value: 'BANNER', labelId: 'admin.assets.category.banners' },
  { value: 'IMAGE', labelId: 'admin.assets.category.images' },
  { value: 'VIDEO', labelId: 'admin.assets.category.videos' },
  { value: 'OTHER', labelId: 'admin.assets.category.other' },
] as const

const CONTENT_TYPE_TO_CATEGORY: Record<string, string> = {
  'image/png': 'IMAGE',
  'image/jpeg': 'IMAGE',
  'image/gif': 'IMAGE',
  'image/webp': 'IMAGE',
  'image/svg+xml': 'IMAGE',
  'video/mp4': 'VIDEO',
  'video/webm': 'VIDEO',
  'video/quicktime': 'VIDEO',
}

export const AdminAssetsPage = () => {
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()

  const [categoryFilter, setCategoryFilter] = useState('ALL')
  const [uploadOpen, setUploadOpen] = useState(false)
  const [previewAsset, setPreviewAsset] = useState<AffiliateAssetSchema | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  const [renameTarget, setRenameTarget] = useState<{ id: string; name: string } | null>(null)
  const [renameName, setRenameName] = useState('')

  // ONE unfiltered query; the category filter is client-side. Asset metadata
  // is a few hundred small rows at worst, and this is what lets the pills
  // carry true per-category counts and switch instantly with no refetch.
  const assetsQuery = useListAssets()
  const { data: assets, isLoading, isError, refetch } = assetsQuery

  // Forward load failures to Sentry — the bare app QueryClient has no global sink.
  // Reports on TERMINAL failure so retries don't multiply Sentry events.
  useReportQueryError(assetsQuery, 'useListAssets failed: ')

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: getListAssetsQueryKey() })

  // A failed delete / rename must reach Sentry AND surface inside its dialog, and
  // must NOT close the dialog (the success-only side-effect).
  const deleteError = useMutationErrorHandler('useDeleteAsset failed: ')
  const renameError = useMutationErrorHandler('useRenameAsset failed: ')

  const deleteMutation = useDeleteAsset({
    mutation: {
      onSuccess: () => {
        invalidate()
        setDeleteTarget(null)
      },
      onError: deleteError.onError,
    },
  })

  const renameMutation = useRenameAsset({
    mutation: {
      onSuccess: () => {
        invalidate()
        setRenameTarget(null)
      },
      onError: renameError.onError,
    },
  })

  const rows = assets ?? []
  const visibleRows =
    categoryFilter === 'ALL' ? rows : rows.filter((asset) => asset.category === categoryFilter)

  // Counts appear with the data — no `0` flash while loading.
  const categoryOptions = CATEGORY_OPTION_KEYS.map((opt) => ({
    value: opt.value,
    label: formatMessage({ id: opt.labelId }),
    count: isLoading
      ? undefined
      : opt.value === 'ALL'
        ? rows.length
        : rows.filter((asset) => asset.category === opt.value).length,
  }))

  const openDelete = (asset: AffiliateAssetSchema) => {
    deleteError.clearError()
    setDeleteTarget({ id: asset.id, name: asset.name })
  }

  const openRename = (asset: AffiliateAssetSchema) => {
    renameError.clearError()
    setRenameTarget({ id: asset.id, name: asset.name })
    setRenameName(asset.name)
  }

  return (
    <Page>
      <Page.Header
        subtitle={<FormattedMessage id="admin.assets.subtitle" />}
        title={<FormattedMessage id="admin.assets.title" />}
        actions={
          <Button
            disabled={isError}
            variant="contained"
            startIcon={
              <CloudArrowUpIcon
                height={18}
                width={18}
              />
            }
            onClick={() => setUploadOpen(true)}
          >
            <FormattedMessage id="admin.assets.upload" />
          </Button>
        }
      />

      <Page.Content>
        {isError ? (
          <InlineQueryError
            data-tst="assets-error"
            messageId="admin.assets.error"
            onRetry={() => void refetch()}
          />
        ) : (
          <>
            <TableFilterBar
              options={categoryOptions}
              value={categoryFilter}
              onValueChange={setCategoryFilter}
            />

            {/* The grid sits directly on the page background — the cards are
                the surfaces. No KPI strip, no titled wrapper: the page header
                names the page and the pills carry the counts. */}
            {isLoading ? (
              <AssetGridSkeleton />
            ) : rows.length === 0 ? (
              <EmptyState
                description={<FormattedMessage id="admin.assets.empty.description" />}
                title={<FormattedMessage id="admin.assets.empty" />}
                action={
                  <Button
                    variant="outlined"
                    startIcon={
                      <CloudArrowUpIcon
                        height={16}
                        width={16}
                      />
                    }
                    onClick={() => setUploadOpen(true)}
                  >
                    <FormattedMessage id="admin.assets.upload" />
                  </Button>
                }
              />
            ) : visibleRows.length === 0 ? (
              <EmptyState
                title={<FormattedMessage id="assets.empty.category" />}
                action={
                  <Button
                    variant="outlined"
                    onClick={() => setCategoryFilter('ALL')}
                  >
                    <FormattedMessage id="assets.empty.showAll" />
                  </Button>
                }
              />
            ) : (
              <AssetGrid>
                {visibleRows.map((asset) => (
                  <AssetCard
                    key={asset.id}
                    category={asset.category}
                    contentType={asset.content_type}
                    downloadUrl={asset.download_url ?? null}
                    fileSize={asset.file_size}
                    name={asset.name}
                    onDelete={() => openDelete(asset)}
                    onPreview={() => setPreviewAsset(asset)}
                    onRename={() => openRename(asset)}
                  />
                ))}
              </AssetGrid>
            )}
          </>
        )}
      </Page.Content>

      {previewAsset && (
        <AssetPreviewDialog
          open
          category={previewAsset.category}
          contentType={previewAsset.content_type}
          downloadUrl={previewAsset.download_url ?? null}
          fileSize={previewAsset.file_size}
          name={previewAsset.name}
          onClose={() => setPreviewAsset(null)}
        />
      )}

      {uploadOpen && (
        <UploadDialog
          onClose={() => setUploadOpen(false)}
          onUploaded={() => {
            setUploadOpen(false)
            invalidate()
          }}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          open
          confirmColor="error"
          confirmLabel={formatMessage({ id: 'admin.assets.confirmYesDelete' })}
          description={formatMessage({ id: 'admin.assets.confirmDelete' })}
          loading={deleteMutation.isPending}
          title={deleteTarget.name}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => deleteMutation.mutate({ assetId: deleteTarget.id })}
        >
          <MutationError
            data-tst="asset-delete-error"
            messageId="admin.assets.deleteError"
            show={deleteError.hasError}
          />
        </ConfirmDialog>
      )}

      {renameTarget && (
        <AppDialog
          open
          width={400}
          onClose={() => setRenameTarget(null)}
        >
          <AppDialog.Header title={<FormattedMessage id="admin.assets.rename" />} />
          <AppDialog.Body>
            <MutationError
              data-tst="asset-rename-error"
              messageId="admin.assets.renameError"
              show={renameError.hasError}
            />
            <TextField
              autoFocus
              label={formatMessage({ id: 'admin.assets.uploadDialog.name' })}
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
            />
          </AppDialog.Body>
          <AppDialog.Footer>
            <Button
              disabled={renameMutation.isPending}
              onClick={() => setRenameTarget(null)}
            >
              <FormattedMessage id="admin.common.cancel" />
            </Button>
            <Button
              variant="contained"
              disabled={
                renameName.trim() === '' ||
                renameName.trim() === renameTarget.name ||
                renameMutation.isPending
              }
              onClick={() =>
                renameMutation.mutate({
                  assetId: renameTarget.id,
                  data: { name: renameName.trim() },
                })
              }
            >
              <FormattedMessage id="admin.common.save" />
            </Button>
          </AppDialog.Footer>
        </AppDialog>
      )}
    </Page>
  )
}

// ─── Upload Dialog ────────────────────────────────────────────────────────────

type UploadDialogProps = {
  onClose: () => void
  onUploaded: () => void
}

const UploadDialog = ({ onClose, onUploaded }: UploadDialogProps) => {
  const { formatMessage } = useIntl()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [name, setName] = useState('')
  const [category, setCategory] = useState('OTHER')
  const [uploading, setUploading] = useState(false)

  const uploadMutation = useUploadAsset()
  // The S3 PUT is a raw fetch, not a TanStack mutation, so the whole multi-step
  // flow (presign → S3 POST → confirm) is wrapped in one try/catch that routes
  // every failure through this handler: forward to Sentry + flip the inline
  // error. A failed upload must never look successful.
  const uploadError = useMutationErrorHandler('useUploadAsset failed: ')

  const handleFileSelect = useEvent((selectedFile: File) => {
    setFile(selectedFile)
    if (name === '') {
      setName(selectedFile.name.replace(/\.[^/.]+$/, ''))
    }
    const autoCategory = CONTENT_TYPE_TO_CATEGORY[selectedFile.type]
    if (autoCategory != null && autoCategory !== '') {
      setCategory(autoCategory)
    }
  })

  const handleDrop = useEvent((e: React.DragEvent) => {
    e.preventDefault()
    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile) {
      handleFileSelect(droppedFile)
    }
  })

  const handleUpload = async () => {
    if (file == null || name.trim() === '') {
      return
    }
    setUploading(true)
    uploadError.clearError()

    try {
      // Step 1 — presign. Throws (caught below) if the BE rejects the request.
      const result = await uploadMutation.mutateAsync({
        data: {
          name: name.trim(),
          category,
          content_type: file.type === '' ? 'application/octet-stream' : file.type,
          file_size: file.size,
        },
      })

      // Step 2 — POST the file to S3 with the presigned fields. fetch only rejects
      // on a network error, so a non-2xx (e.g. 403 expired policy) must be turned
      // into a throw — otherwise a failed S3 upload would fall through to success.
      const formData = new FormData()
      for (const [key, value] of Object.entries(result.upload_fields)) {
        formData.append(key, String(value))
      }
      formData.append('file', file)

      const s3Response = await fetch(result.upload_url, { method: 'POST', body: formData })
      if (!s3Response.ok) {
        throw new Error(`S3 upload failed with status ${s3Response.status}`)
      }

      // Step 3 — clear the spinner BEFORE `onUploaded()` unmounts the dialog;
      // a `finally`-block setState after unmount would warn (and on the success
      // path the dialog never sees the cleared state anyway). On the catch
      // branch the dialog stays mounted, so we clear there too.
      setUploading(false)
      onUploaded()
    } catch (error: unknown) {
      setUploading(false)
      uploadError.onError(error)
    }
  }

  return (
    <AppDialog
      open
      width={460}
      onClose={onClose}
    >
      <AppDialog.Header title={<FormattedMessage id="admin.assets.uploadDialog.title" />} />

      <AppDialog.Body>
        <MutationError
          data-tst="asset-upload-error"
          messageId="admin.assets.uploadFailed"
          show={uploadError.hasError}
        />

        <div
          className="flex h-[120px] cursor-pointer items-center justify-center rounded-sm border-2 border-dashed border-divider transition-colors hover:border-primary-main"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            hidden
            type="file"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) {
                handleFileSelect(f)
              }
            }}
          />
          <Typography
            color="text.secondary"
            variant="body2"
          >
            {file ? file.name : formatMessage({ id: 'admin.assets.uploadDialog.dropzone' })}
          </Typography>
        </div>

        <TextField
          label={formatMessage({ id: 'admin.assets.uploadDialog.name' })}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <SelectField
          label={formatMessage({ id: 'admin.assets.uploadDialog.category' })}
          value={category}
          options={[
            { value: 'BANNER', label: formatMessage({ id: 'admin.assets.category.banner' }) },
            { value: 'IMAGE', label: formatMessage({ id: 'admin.assets.category.image' }) },
            { value: 'VIDEO', label: formatMessage({ id: 'admin.assets.category.video' }) },
            { value: 'OTHER', label: formatMessage({ id: 'admin.assets.category.other' }) },
          ]}
          onValueChange={setCategory}
        />
      </AppDialog.Body>

      <AppDialog.Footer>
        <Button
          disabled={uploading}
          onClick={onClose}
        >
          <FormattedMessage id="admin.common.cancel" />
        </Button>
        <Button
          disabled={file == null || name.trim() === '' || uploading}
          variant="contained"
          onClick={() => {
            void handleUpload()
          }}
        >
          {uploading
            ? formatMessage({ id: 'admin.assets.uploadDialog.uploading' })
            : formatMessage({ id: 'admin.assets.upload' })}
        </Button>
      </AppDialog.Footer>
    </AppDialog>
  )
}
